import { useState, useRef, useCallback, useEffect } from 'react';
import { useTheme } from '../../zustand/ThemeStore';

interface DualRangeSliderProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  step?: number;
  formatValue?: (value: number) => string;
  className?: string;
}

export default function DualRangeSlider({
  min,
  max,
  value,
  onChange,
  step = 1,
  formatValue,
  className = ''
}: DualRangeSliderProps) {
  const isDarkMode = useTheme((state) => state.theme);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<'min' | 'max' | null>(null);

  const [minValue, maxValue] = value;

  // Calculate positions as percentages
  const getPosition = (val: number) => {
    return ((val - min) / (max - min)) * 100;
  };

  const minPosition = getPosition(minValue);
  const maxPosition = getPosition(maxValue);

  // Convert mouse position to value
  const getValueFromPosition = useCallback((clientX: number) => {
    if (!sliderRef.current) return 0;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const rawValue = min + percentage * (max - min);
    
    // Round to nearest step
    return Math.round(rawValue / step) * step;
  }, [min, max, step]);

  const handleMouseDown = useCallback((type: 'min' | 'max') => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(type);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const newValue = getValueFromPosition(e.clientX);
    
    if (isDragging === 'min') {
      const clampedMin = Math.max(min, Math.min(newValue, maxValue - step));
      onChange([clampedMin, maxValue]);
    } else {
      const clampedMax = Math.min(max, Math.max(newValue, minValue + step));
      onChange([minValue, clampedMax]);
    }
  }, [isDragging, getValueFromPosition, min, max, step, minValue, maxValue, onChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
  }, []);

  const handleTrackClick = useCallback((e: React.MouseEvent) => {
    if (isDragging) return;
    
    const newValue = getValueFromPosition(e.clientX);
    const midPoint = (minValue + maxValue) / 2;
    
    if (newValue < midPoint) {
      // Click closer to min handle
      const clampedMin = Math.max(min, Math.min(newValue, maxValue - step));
      onChange([clampedMin, maxValue]);
    } else {
      // Click closer to max handle
      const clampedMax = Math.min(max, Math.max(newValue, minValue + step));
      onChange([minValue, clampedMax]);
    }
  }, [isDragging, getValueFromPosition, minValue, maxValue, min, max, step, onChange]);

  // Global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Default format function
  const defaultFormatValue = (val: number) => {
    if (val < 60) {
      return `${val}min`;
    }
    const hours = Math.floor(val / 60);
    const mins = val % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const format = formatValue || defaultFormatValue;

  return (
    <div className={`w-full ${className}`}>
      {/* Slider Track */}
      <div className="relative mb-4">
        <div
          ref={sliderRef}
          onClick={handleTrackClick}
          className={`relative h-3 rounded-full cursor-pointer transition-colors ${
            isDarkMode ? 'bg-[#1a2424]' : 'bg-gray-200'
          }`}
        >
          {/* Active Range */}
          <div
            className="absolute h-3 bg-gradient-to-r from-[#1a9f6b] to-[#148856] rounded-full shadow-sm"
            style={{
              left: `${minPosition}%`,
              width: `${maxPosition - minPosition}%`
            }}
          />
          
          {/* Min Handle */}
          <div
            className={`absolute w-6 h-6 -mt-1.5 rounded-full cursor-grab active:cursor-grabbing shadow-lg transition-all duration-200 transform ${
              isDragging === 'min' ? 'scale-110 shadow-xl' : 'hover:scale-105'
            } ${
              isDarkMode 
                ? 'bg-white border-2 border-[#1a9f6b] hover:shadow-[#1a9f6b]/30' 
                : 'bg-white border-2 border-[#1a9f6b] hover:shadow-[#1a9f6b]/20'
            }`}
            style={{ 
              left: `calc(${minPosition}% - 12px)`,
              zIndex: isDragging === 'min' ? 3 : 2
            }}
            onMouseDown={handleMouseDown('min')}
          >
            {/* Handle glow effect */}
            <div className={`absolute inset-0 rounded-full transition-opacity ${
              isDragging === 'min' ? 'bg-[#1a9f6b]/30 opacity-100' : 'opacity-0'
            }`} />
          </div>
          
          {/* Max Handle */}
          <div
            className={`absolute w-6 h-6 -mt-1.5 rounded-full cursor-grab active:cursor-grabbing shadow-lg transition-all duration-200 transform ${
              isDragging === 'max' ? 'scale-110 shadow-xl' : 'hover:scale-105'
            } ${
              isDarkMode 
                ? 'bg-white border-2 border-[#1a9f6b] hover:shadow-[#1a9f6b]/30' 
                : 'bg-white border-2 border-[#1a9f6b] hover:shadow-[#1a9f6b]/20'
            }`}
            style={{ 
              left: `calc(${maxPosition}% - 12px)`,
              zIndex: isDragging === 'max' ? 3 : 2
            }}
            onMouseDown={handleMouseDown('max')}
          >
            {/* Handle glow effect */}
            <div className={`absolute inset-0 rounded-full transition-opacity ${
              isDragging === 'max' ? 'bg-[#1a9f6b]/30 opacity-100' : 'opacity-0'
            }`} />
          </div>
        </div>
      </div>

      {/* Value Display */}
      <div className="flex justify-between items-center">
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
          isDarkMode ? 'bg-[#050a0a] border border-[#1a2424]' : 'bg-gray-50 border border-gray-200'
        }`}>
          <span className={`text-xs font-medium ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Min:
          </span>
          <span className={`text-sm font-semibold ${
            isDarkMode ? 'text-white' : 'text-[#053333]'
          }`}>
            {format(minValue)}
          </span>
        </div>
        
        <div className={`px-2 py-1 ${
          isDarkMode ? 'text-[#1a9f6b]' : 'text-[#148856]'
        }`}>
          ←→
        </div>
        
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
          isDarkMode ? 'bg-[#050a0a] border border-[#1a2424]' : 'bg-gray-50 border border-gray-200'
        }`}>
          <span className={`text-xs font-medium ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Max:
          </span>
          <span className={`text-sm font-semibold ${
            isDarkMode ? 'text-white' : 'text-[#053333]'
          }`}>
            {format(maxValue)}
          </span>
        </div>
      </div>

      {/* Range Indicators */}
      <div className="mt-3 flex justify-between text-xs">
        <span className={`${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {format(min)}
        </span>
        <span className={`${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {format(max)}
        </span>
      </div>
    </div>
  );
}